import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Receipt,
  X,
  Search,
  RotateCcw,
  Check,
  Clock,
  Image as ImageIcon,
} from "lucide-react";
import api, { IMAGE_BASE_URL, ADMIN_IMAGE_URL } from "../api/Api";

const DriverExpenseHistory = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [fullImage, setFullImage] = useState(null);

  // --- FILTER STATES & 3-DAY DEFAULT LOGIC ---
  const getPastDateStr = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split("T")[0];
  };

  const todayStr = new Date().toISOString().split("T")[0];
  const threeDaysAgoStr = getPastDateStr(3);

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState(threeDaysAgoStr); 
  const [toDate, setToDate] = useState(todayStr); 
  const fetchHistory = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;
    try {
      setLoading(true);
      const response = await api.get(
        `/expense/otherexp-history/${driverRegNo}`
      );
      if (response.data.success) {
        // console.log("Expense history data:", response.data.data);
        setLogs(response.data.data);
        setFilteredLogs(response.data.data);
      }
    } catch (error) {
      console.error("History fetch error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  // --- SEARCH & DATE FILTER LOGIC ---
  useEffect(() => {
    let result = logs;

    if (fromDate) {
      result = result.filter(
        (log) => new Date(log.date).toISOString().split("T")[0] >= fromDate
      );
    }
    if (toDate) {
      result = result.filter(
        (log) => new Date(log.date).toISOString().split("T")[0] <= toDate
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (log) =>
          log.expense_type.toLowerCase().includes(term) ||
          log.amount.toString().includes(term) ||
          log.expense_no.toLowerCase().includes(term)
      );
    }
    setFilteredLogs(result);
  }, [searchTerm, fromDate, toDate, logs]);

  const resetFilters = () => {
    setSearchTerm("");
    setFromDate(threeDaysAgoStr); // Resets back to 3 days ago
    setToDate(todayStr); // Resets back to today
  };

  const getImageUrl = (filename, type) => {
    if (!filename) return "https://placehold.co/400?text=No+Img";
    if (type === "payment_proof") {
      return `${ADMIN_IMAGE_URL}/uploads/expense_receipt/${filename}`;
    }
    return `${IMAGE_BASE_URL}/uploads/expense_receipt/${filename}`;
  };

  return (
    <div className="android-app-container bg-white min-vh-100 font-sans overflow-x-hidden">
     <div className="app-bar text-white pb-5" style={{background: "linear-gradient(90deg, #09b818, #0077ff)", borderRadius: "0 0 24px 24px"}}>
        <div className="d-flex flex-column justify-content-center px-4 pt-4 pb-4">
          <h1 className="h4 mb-1 fw-bold tracking-tight text-uppercase">Expense Activity</h1>
          <span className="subtitle-text opacity-55 d-flex align-items-center gap-1">
            {filteredLogs.length} Records Found
          </span>
        </div>
      </div>

      <div
        className="px-3"
        style={{ marginTop: "-50px", position: "relative", zIndex: 10 }}
      >
        <div className="card border-0 shadow-sm rounded-4 bg-white p-3">
          {/* Search Bar */}
          <div className="material-search-bar mb-3 d-flex align-items-center px-3 py-2 rounded-pill bg-light">
            <Search size={18} className="text-muted me-2" />
            <input
              type="text"
              className="border-0 bg-transparent flex-grow-1 text-dark fw-medium"
              placeholder="Search type or amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ outline: "none", fontSize: "14px" }}
            />
            {searchTerm && (
              <button
                className="icon-btn text-muted p-0"
                onClick={() => setSearchTerm("")}
              >
                <X size={18} />
              </button>
            )}
          </div>

          <div className="row g-2 align-items-center">
            <div className="col-5">
              <div className="material-date-input bg-light rounded-3 px-2 py-1 w-100 h-100 d-flex flex-column justify-content-center">
                <span className="label-tiny text-muted fw-bold mb-1">FROM</span>
                <input
                  type="date"
                  max={todayStr}
                  className="w-100 border-0 bg-transparent fw-semibold text-dark p-0 m-0"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
            </div>
            <div className="col-5">
              <div className="material-date-input bg-light rounded-3 px-2 py-1 w-100 h-100 d-flex flex-column justify-content-center">
                <span className="label-tiny text-muted fw-bold mb-1">TO</span>
                <input
                  type="date"
                  max={todayStr}
                  className="w-100 border-0 bg-transparent fw-semibold text-dark p-0 m-0"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
            <div className="col-2 d-flex justify-content-end">
              <button
                className="btn btn-light rounded-3 d-flex align-items-center justify-content-center border-0 shadow-none text-danger w-100"
                style={{ height: "48px", background: "#fee2e2" }}
                onClick={resetFilters}
                aria-label="Reset Filters"
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- LIST AREA --- */}
      <div className="container-fluid px-3 pt-4 pb-5 mb-5">
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            ></div>
            <span className="mt-3 fw-medium text-muted">
              Loading expenses...
            </span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4 text-center">
            <div className="rounded-circle bg-white shadow-sm p-4 mb-3 d-flex align-items-center justify-content-center">
              <Receipt size={40} className="text-muted opacity-50" />
            </div>
            <h6 className="fw-bold text-dark mb-1">No expenses found</h6>
            <p className="text-muted small">
              Try adjusting your date range or search.
            </p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                onClick={() => setSelectedLog(log)}
                className="material-list-card card border-0 shadow-sm rounded-4 overflow-hidden"
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className={`icon-container rounded-circle bg-${log.status_color}-subtle text-${log.status_color} d-flex align-items-center justify-content-center`}
                        style={{ width: "48px", height: "48px" }}
                      >
                        <Receipt size={24} />
                      </div>
                      <div>
                        <h6
                          className="mb-0 fw-bold text-dark text-truncate"
                          style={{ maxWidth: "160px" }}
                        >
                          {log.expense_type}
                        </h6>
                        <span className="text-muted small d-flex align-items-center gap-1 mt-1 fw-medium">
                          <Clock size={12} />
                          {new Date(log.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="text-end">
                      <h6 className="fw-bold text-dark mb-1 fs-5">
                        ₹{Number(log.amount).toFixed(2)}
                      </h6>
                      <span
                        className={`material-chip chip-${log.status_color}`}
                      >
                        {log.status}
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                    <div
                      className="small text-muted d-flex align-items-center gap-1 text-truncate"
                      style={{ maxWidth: "70%" }}
                    >
                      <MapPin
                        size={12}
                        className="text-primary flex-shrink-0"
                      />
                      <span className="text-truncate fw-medium">
                        {log.location || "Location N/A"}
                      </span>
                    </div>
                    <span
                      className="text-dark fw-bold text-uppercase"
                      style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                    >
                       {log.expense_no}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- NATIVE BOTTOM SHEET --- */}
      <div
        className={`bottom-sheet-backdrop ${selectedLog ? "open" : ""}`}
        onClick={() => setSelectedLog(null)}
      >
        <div
          className="bottom-sheet surface-color"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Handle */}
          <div
            className="d-flex justify-content-center pt-2 pb-3 cursor-pointer"
            onClick={() => setSelectedLog(null)}
          >
            <div className="drag-handle rounded-pill bg-secondary opacity-25"></div>
          </div>

          {selectedLog && (
            <div className="bottom-sheet-content px-4 pb-5">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h4 className="fw-bold text-dark mb-0">Expense Details</h4>
                  <span className="text-primary small fw-semibold">
                    #{selectedLog.expense_no}
                  </span>
                </div>
                <button
                  className="btn btn-light rounded-circle p-2 shadow-none border-0"
                  onClick={() => setSelectedLog(null)}
                >
                  <X size={20} className="text-dark" />
                </button>
              </div>

              {/* Amount & Status Card */}
              <div
                className={`status-card rounded-4 p-4 mb-4 d-flex justify-content-between align-items-center bg-${selectedLog.status_color}-subtle border border-${selectedLog.status_color}-subtle`}
              >
                <div>
                  <span className="text-muted small fw-bold text-uppercase d-block mb-1" style={{ letterSpacing: "0.5px" }}>
                    Total Amount
                  </span>
                  <h2
                    className={`fw-bolder mb-0 text-${selectedLog.status_color}`}
                  >
                    ₹{selectedLog.amount}
                  </h2>
                </div>
                <div
                  className={`status-badge bg-${selectedLog.status_color} text-white fw-bold rounded-pill px-3 py-2 text-uppercase`}
                  style={{ fontSize: "12px", letterSpacing: "1px" }}
                >
                  {selectedLog.status}
                </div>
              </div>

              {/* Info Grid */}
              <div className="info-grid bg-white border rounded-4 p-3 mb-4 shadow-sm">
                <div className="row g-0">
                  <div className="col-12 border-bottom pb-2 mb-2">
                    <span className="label-tiny text-muted fw-bold d-block">
                      EXPENSE TYPE
                    </span>
                    <span className="fw-semibold text-dark fs-6">
                      {selectedLog.expense_type}
                    </span>
                  </div>
                  <div className="col-6 border-end pe-2">
                    <span className="label-tiny text-muted fw-bold d-block">
                      LOCATION
                    </span>
                    <span
                      className="fw-semibold text-dark text-truncate d-block"
                      title={selectedLog.location}
                    >
                      {selectedLog.location || "N/A"}
                    </span>
                  </div>
                  <div className="col-6 ps-3">
                    <span className="label-tiny text-muted fw-bold d-block">
                      DATE
                    </span>
                    <span className="fw-semibold text-dark">
                      {new Date(selectedLog.date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="col-12 border-top pt-2 mt-2">
                    <span className="label-tiny text-muted fw-bold d-block">
                      DRIVER NOTE
                    </span>
                    <p className="mb-0 fw-medium text-dark small text-wrap">
                      {selectedLog.expense_note || "No notes provided."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Uploads Section */}
              <h6 className="fw-bold text-dark mb-3">Attachments</h6>
              <div className="row g-3 mb-4">
                {[
                  { img: selectedLog.receipt_image, label: "Receipt" },
                  { img: selectedLog.qr_image, label: "Payment QR" },
                ].map(
                  (item, idx) =>
                    item.img && (
                      <div className="col-6" key={idx}>
                        <div
                          className="attachment-card position-relative rounded-4 overflow-hidden border shadow-sm"
                          onClick={() =>
                            setFullImage(getImageUrl(item.img, "driver"))
                          }
                        >
                          <img
                            src={getImageUrl(item.img, "driver")}
                            className="w-100 object-fit-cover"
                            style={{ height: "120px" }}
                            alt={item.label}
                          />
                          <div className="position-absolute bottom-0 w-100 p-2 image-gradient-overlay text-white">
                            <span className="small fw-semibold d-flex align-items-center gap-1">
                              <ImageIcon size={14} /> {item.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                )}
                {!selectedLog.receipt_image && !selectedLog.qr_image && (
                  <div className="col-12 text-center p-4 bg-light rounded-4 border border-dashed">
                    <span className="text-muted fw-medium small">
                      No attachments uploaded
                    </span>
                  </div>
                )}
              </div>

              {/* Verification Section */}
              {(selectedLog.payment_proof_image ||
                selectedLog.internal_notes) && (
                <>
                  <h6 className="fw-bold text-dark mb-3">Admin Verification</h6>
                  <div className="bg-success-subtle border border-success border-opacity-25 rounded-4 p-3 mb-4">
                    <div className="d-flex gap-3 align-items-start">
                      {selectedLog.payment_proof_image && (
                        <div
                          className="flex-shrink-0 position-relative rounded-3 overflow-hidden shadow-sm border border-white"
                          style={{ width: "80px", height: "80px" }}
                          onClick={() =>
                            setFullImage(
                              getImageUrl(
                                selectedLog.payment_proof_image,
                                "payment_proof"
                              )
                            )
                          }
                        >
                          <img
                            src={getImageUrl(
                              selectedLog.payment_proof_image,
                              "payment_proof"
                            )}
                            className="w-100 h-100 object-fit-cover"
                            alt="Proof"
                          />
                          <div className="position-absolute top-0 end-0 bg-success text-white p-1 rounded-bottom-start">
                            <Check size={12} />
                          </div>
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <span className="text-success fw-bold small text-uppercase mb-1 d-block">
                          Admin Note
                        </span>
                        <p className="mb-0 text-dark fw-medium small">
                          {selectedLog.internal_notes ||
                            "Payment verified successfully."}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <button
                className="btn btn-primary w-100 rounded-pill py-3 fw-bold mt-2 fs-6 shadow-sm"
                onClick={() => setSelectedLog(null)}
              >
                Close Details
              </button>
            </div>
          )}
        </div>
      </div>

      {/* --- FULL IMAGE LIGHTBOX --- */}
      {fullImage && (
        <div
          className="lightbox-overlay d-flex align-items-center justify-content-center"
          onClick={() => setFullImage(null)}
        >
          <button
            className="btn text-white position-absolute top-0 end-0 m-4 p-2 shadow-none border-0"
            onClick={() => setFullImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={fullImage}
            className="img-fluid rounded-3 max-w-90 max-h-90 zoom-in-animation shadow-lg border border-secondary"
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* --- STYLES --- */}
      <style>{`
        /* Colors & Theme */
        :root {
          --md-primary: #0056D2; /* Adjusted to match your image's blue header */
          --md-surface: #f4f6f8;
          --md-on-surface: #212529;
          --md-outline: #e9ecef;
        }

        .bg-surface { background-color: var(--md-surface); }
        .bg-primary { background-color: var(--md-primary) !important; }
        
        /* App Bar */
        .app-bar { padding-top: env(safe-area-inset-top); }
        .icon-btn { background: transparent; border: none; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 50%; width: 36px; height: 36px; transition: background 0.2s; }
        .icon-btn:active { background: rgba(0,0,0,0.05); }
        .subtitle-text { font-size: 13px; font-weight: 500; }

        /* Typography & Forms */
        .label-tiny { font-size: 9px; letter-spacing: 0.8px; }
        .material-date-input input[type="date"] { font-size: 13px; }
        .material-date-input input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; padding-left: 5px; }
        .material-date-input input[type="date"]:focus { outline: none; }
        
        /* Cards & List */
        .material-list-card { transition: transform 0.15s ease, box-shadow 0.15s ease; cursor: pointer; border: 1px solid var(--md-outline) !important; }
        .material-list-card:active { transform: scale(0.97); background-color: #f8f9fa; }
        
        /* Chips */
        .material-chip { font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 12px; display: inline-block; text-transform: uppercase; letter-spacing: 0.5px; }
        .chip-warning { background-color: #fff3cd; color: #856404; }
        .chip-success { background-color: #d1e7dd; color: #0f5132; }
        .chip-danger { background-color: #f8d7da; color: #842029; }
        .chip-primary { background-color: #cfe2ff; color: #084298; }
        
        /* Bootstrap color overrides */
        .bg-warning { background-color: #ffc107 !important; color: #000; }
        .bg-danger { background-color: #dc3545 !important; }
        .bg-success { background-color: #198754 !important; }
        
        .bg-warning-subtle { background-color: #fff8e1 !important; color: #f57f17 !important; }
        .bg-danger-subtle { background-color: #f8d7da !important; color: #842029 !important; }
        .bg-success-subtle { background-color: #d1e7dd !important; color: #0f5132 !important; }
        
        .text-warning { color: #f57f17 !important; }
        .text-danger { color: #dc3545 !important; }
        .text-success { color: #198754 !important; }

        /* =========================================
           FIXED BOTTOM SHEET & LIGHTBOX FOR WEBVIEW
           ========================================= */

        /* Bottom Sheet */
        .bottom-sheet-backdrop { 
          position: fixed; 
          inset: 0; 
          background: rgba(0,0,0,0.75); /* Darkened to replace blur */
          z-index: 9999; /* Boosted */
          display: flex; 
          align-items: flex-end; 
          opacity: 0; 
          visibility: hidden; 
          transition: opacity 0.3s, visibility 0.3s; 
          /* backdrop-filter: blur(3px); REMOVED: Breaks WebViews */
          transform: translateZ(0); /* Hardware Acceleration */
          -webkit-transform: translateZ(0);
        }
        
        .bottom-sheet-backdrop.open { 
          opacity: 1; 
          visibility: visible; 
        }
        
        .bottom-sheet { 
          background: white; 
          width: 100%; 
          height: "auto !important", 
          border-radius: 28px 28px 0 0; 
          display: flex; 
          flex-direction: column; 
          z-index: 10000; /* Boosted */
          transform: translateY(100%) translateZ(0); 
          -webkit-transform: translateY(100%) translateZ(0);
          transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), -webkit-transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1); 
        }
        
        .bottom-sheet-backdrop.open .bottom-sheet { 
          transform: translateY(0) translateZ(0); 
          -webkit-transform: translateY(0) translateZ(0);
        }
        
        .bottom-sheet-content { 
          overflow-y: auto; 
          flex-grow: 1; 
          padding-bottom: env(safe-area-inset-bottom); 
        }
        
        .drag-handle { width: 40px; height: 5px; }
        .image-gradient-overlay { background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%); }
        .border-dashed { border-style: dashed !important; border-width: 2px !important; border-color: #dee2e6 !important; }

        /* Lightbox */
        .lightbox-overlay { 
          position: fixed; 
          inset: 0; 
          background: rgba(0,0,0,0.95); 
          z-index: 99999; /* Boosted to max to sit over the modal */
          /* backdrop-filter: blur(5px); REMOVED */
          transform: translateZ(0); 
          -webkit-transform: translateZ(0);
        }
        .max-w-90 { max-width: 90vw; }
        .max-h-90 { max-height: 90%; }
        .zoom-in-animation { animation: zoomIn 0.25s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; }
        
        @keyframes zoomIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        
        /* Utility */
        ::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default DriverExpenseHistory;